import {DialogActions, DialogContent, Typography} from '@mui/material';
import {GridColDef, GridRowParams} from '@mui/x-data-grid';
import {useEffect, useMemo, useRef, useState} from 'react';
import {useDispatch, useSelector} from 'react-redux';
import {t} from 'i18next';
import {useNavigate} from 'react-router-dom';
import {
  closeDialog,
  continueNCloseDialog,
  SelectionListItemType,
  setOpenCount,
} from '../../../../redux/slices/reportsDialogSlice';
import AurDataGrid from '../../../common/aur-data-grid/AurDataGrid';
import AurPagination from '../../../common/aur-pagination/AurPagination';
import {AurTextField} from '../../../common/aur-text-field/AurTextField';
import {RootState} from '../../../../redux/store';
import {storeProjectNWorkSpaceId} from '../../../../redux/slices/reportsSlice';
import AurDataGridNoRowsOverlay from '../../../common/aur-data-grid-no-rows-overlay/AurDataGridNoRowsOverlay';
import AurLoadingIndicator from '../../../common/aur-loading-indicator/AurLoadingIndictor';
import {ReactComponent as RadioCheckedIcon} from '../../../../assests/svg/radio-checked.svg';
import {ReactComponent as RadioUnCheckedIcon} from '../../../../assests/svg/radio-unchecked.svg';
import {
  TABLE_PAGE_SIZE_OPTION,
  TABLE_PER_PAGE_DEFAULT_NO_ROW,
} from '../../../../constants/constants';
import {useAnalytics} from '../../../../hooks/useAnalytics';
import {
  GA_CLICK_TYPES,
  GA_EVENT_NAMES,
  GA_REGIONS,
  GA_SUBPAGE_TYPE,
} from '../../../../constants/analyticsConstants';

interface ProjectWorkSpaceSelectionProps {
  heading: string;
  subHeading: string;
  openFrom?: string;
  setParentCloseCB?: (cb: () => void) => void;
  hideNonConsilioProjects?: boolean;
}

type AurDataGridColDef = GridColDef & {
  isSearchBoxRequired?: boolean;
  isBorderNotRequired?: boolean;
};

type SelectionListType = 'PROJECT_LIST' | 'WORKSPACE_LIST';

const ProjectWorkSpaceSelection = (props: ProjectWorkSpaceSelectionProps) => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const {trackGAEvent} = useAnalytics();

  const {heading, subHeading, openFrom, hideNonConsilioProjects} = props;

  const [selectedListType, setSelectedListType] =
    useState<SelectionListType>('PROJECT_LIST');
  const [focusedTextField, setFocusedTextField] =
    useState<SelectionListType | null>(null);
  const [selectedProject, setSelectedProject] =
    useState<SelectionListItemType | null>(null);
  const [selectedWorkspace, setSelectedWorkspace] =
    useState<SelectionListItemType | null>(null);

  const [currentPageNo, setCurrentPageNo] = useState<number>(1);
  const [noOfItemPerPage, setNoOfItemPerPage] = useState<number>(
    TABLE_PER_PAGE_DEFAULT_NO_ROW
  );
  const [searchTerm, setSearchTerm] = useState<string>('');

  const projectButtonRef = useRef<HTMLInputElement | null>(null);
  const workspaceButtonRef = useRef<HTMLInputElement | null>(null);

  const {
    openCount: dialogOpenCount,
    isProjectLoading,
    projectList: projects,
    workspaceList,
    projectWorkSpaceMap,
  } = useSelector((state: RootState) => state.dialog);

  const reportPageOpenFrom = useSelector(
    (state: RootState) => state.reports.pageOpenFrom
  );

  const projectList = useMemo(() => {
    if (hideNonConsilioProjects) {
      return projects.filter((project) => !project.isNonConsilioWorkflow);
    }
    return projects;
  }, [projects]);

  const {
    projectId,
    workspaceId,
    selectedProjectName: projectName,
    selectedWorkSpaceName: workspaceName,
  } = useSelector((state: RootState) => state.reports);

  useEffect(() => {
    if (props.setParentCloseCB) {
      props.setParentCloseCB(handleCancelClick);
    }

    if (projectId && workspaceId) {
      setSelectedProject({
        id: projectId,
        name: projectName ?? '',
      });

      setSelectedWorkspace({
        id: workspaceId,
        name: workspaceName ?? '',
        parentId: projectId,
      });
    } else if (projectId) {
      setSelectedProject({
        id: projectId.toLowerCase(),
        name: projectName ?? '',
      });
    }
  }, []);

  useEffect(() => {
    // If a project ID is specified and the project list exists,
    // set the selected project and switch to the workspace selection.
    if (projectId && projectList) {
      setSelectedProject({
        id: projectId.toLowerCase(),
        name: projectName ?? '',
      });

      setSelectedListType('WORKSPACE_LIST');
      workspaceButtonRef.current?.focus();
    }
  }, [projectId, projectList]);

  useEffect(() => {
    setCurrentPageNo(1);
    setNoOfItemPerPage(TABLE_PER_PAGE_DEFAULT_NO_ROW);
    setSearchTerm('');
  }, [selectedListType]);

  const {tableRows, totalRowCount} = useMemo(() => {
    let data: SelectionListItemType[] = [];
    let rowCount = 0;
    switch (selectedListType) {
      case 'PROJECT_LIST':
        data = [...projectList];
        rowCount = projectList.length;
        break;
      case 'WORKSPACE_LIST':
        if (selectedProject) {
          data = [...(projectWorkSpaceMap[selectedProject.id] ?? [])];
        } else data = [...workspaceList];
        rowCount = data.length;
        break;
      default:
        break;
    }

    if (searchTerm !== '') {
      data = data.filter((item) =>
        item.name.toLowerCase().includes(searchTerm.toLowerCase())
      );
      rowCount = data.length;
    }

    const startIndex = (currentPageNo - 1) * noOfItemPerPage;
    data = data.slice(startIndex, startIndex + noOfItemPerPage);

    return {tableRows: data, totalRowCount: rowCount};
  }, [
    selectedListType,
    projectList,
    workspaceList,
    projectWorkSpaceMap,
    selectedProject,
    searchTerm,
    currentPageNo,
    noOfItemPerPage,
  ]);

  const selectedRowId = useMemo(() => {
    switch (selectedListType) {
      case 'PROJECT_LIST':
        return selectedProject?.id ?? null;
      case 'WORKSPACE_LIST':
        return selectedWorkspace?.id ?? null;
      default:
        return null;
    }
  }, [selectedListType, selectedProject, selectedWorkspace]);

  const perPageOptions = useMemo(() => {
    const options = TABLE_PAGE_SIZE_OPTION.filter(
      (option) => option <= totalRowCount
    );
    const additionalOption = TABLE_PAGE_SIZE_OPTION.find(
      (option) => option >= totalRowCount
    );
    if (additionalOption && !options?.includes(additionalOption)) {
      options?.push(additionalOption);
    }
    return options;
  }, [totalRowCount]);

  const focusContinueButton = () => {
    setTimeout(() => {
      const button = document.querySelector(
        '[data-testid="pagination-continue-button"]'
      );
      if (button) {
        (button as HTMLButtonElement).focus();
      }
    }, 200);
  };

  const handleContinueClick = () => {
    if (selectedProject && selectedWorkspace) {
      dispatch(
        storeProjectNWorkSpaceId({
          projectId: selectedProject.id,
          workspaceId: selectedWorkspace.id,
          projectName: selectedProject.name,
          workspaceName: selectedWorkspace.name,
          projectCode: selectedProject.code,
        })
      );
    }
    dispatch(continueNCloseDialog());
    dispatch(setOpenCount(2));
    trackGAEvent({
      eventName: GA_EVENT_NAMES.CLICK,
      click: {
        name: t('gaTracking.global.continue'),
        type: GA_CLICK_TYPES.BUTTON,
        region: GA_REGIONS.MODAL,
        action: t('gaTracking.reports.changeProjectandWorkspaceComplete'),
      },
      subPage: {
        subpage_name: t('gaTracking.reports.changeProjectandWorkspace'),
        subpage_type: GA_SUBPAGE_TYPE.MODAL,
      },
    });
  };

  const handleCancelClick = () => {
    trackGAEvent({
      eventName: GA_EVENT_NAMES.CLICK,
      click: {
        name: t('gaTracking.global.cancel'),
        type: GA_CLICK_TYPES.BUTTON,
        region: GA_REGIONS.MODAL,
        action: t('gaTracking.reports.changeProjectandWorkspaceClosedModal'),
      },
      subPage: {
        subpage_name: t('gaTracking.reports.changeProjectandWorkspace'),
        subpage_type: GA_SUBPAGE_TYPE.MODAL,
      },
    });
    if (dialogOpenCount === 1) {
      navigate(reportPageOpenFrom ?? '/welcome-single-client', {replace: true});
    }
    dispatch(closeDialog());
  };

  const handleRowSelection = (params: GridRowParams) => {
    // Preventing selected event when row is already selected
    if (selectedRowId !== params.id) {
      switch (selectedListType) {
        case 'PROJECT_LIST':
          setSelectedProject(params.row);
          setSelectedWorkspace(null);
          setSelectedListType('WORKSPACE_LIST');
          workspaceButtonRef.current?.focus();
          break;
        case 'WORKSPACE_LIST':
          if (!selectedProject) {
            const project = projectList.find(
              (item) => item.id === params.row.parentId
            );
            if (project) {
              setSelectedProject(project);
            }
          }
          setSelectedWorkspace(params.row);
          focusContinueButton();
          break;
      }
    }
  };

  const handleProjectWorkspaceButtonClick = (type: SelectionListType) => {
    setFocusedTextField(type);
    setSelectedListType(type);
  };

  const onSearch = (searchTerm: string) => {
    if (searchTerm) {
      setCurrentPageNo(1);
      setSearchTerm(searchTerm);
    } else {
      setCurrentPageNo(1);
      setSearchTerm('');
    }
  };

  const handlePageSizeChange = (pageSize: number) => {
    setCurrentPageNo(1);
    setNoOfItemPerPage(pageSize);
  };

  const columns: AurDataGridColDef[] = [
    {
      field: 'name',
      headerName: t(
        `reports.selectionModal.${selectedListType === 'WORKSPACE_LIST' ? 'workspace' : 'projectName'}`
      ),
      headerClassName: 'custom-header-report-grid',
      flex: 1,
      isSearchBoxRequired: true,
      sortable: false,
      renderCell: (params) => {
        const isSelected = params.id === selectedRowId;
        return (
          <div className="row-container" data-testid={`row-${params.id}`}>
            <div className="img-container">
              {isSelected ? <RadioCheckedIcon /> : <RadioUnCheckedIcon />}
            </div>
            <Typography className="typo-text body3_size list-text">
              {params.value}
            </Typography>
          </div>
        );
      },
    },
  ];

  const AurDataGridLoadingOverlay = () => {
    if (!isProjectLoading) return <AurDataGridNoRowsOverlay />;
    return (
      <div className="grid-loading">
        <AurLoadingIndicator />
      </div>
    );
  };

  return (
    <>
      <DialogContent className="model-content">
        <>
          <Typography className="typo-text headline3_size heading">
            {heading}
          </Typography>
          <Typography className="typo-text body2_size subheading">
            {subHeading}
          </Typography>
          <div className="selection-buttons-row">
            <AurTextField
              variant="filled"
              placeholder={t('reports.selectionModal.chooseFromList')}
              className={`aur-textfield custom-text-field ${selectedListType === 'PROJECT_LIST' ? 'focused' : ''}`}
              label={t('reports.selectionModal.selectProject')}
              value={selectedProject?.name ?? ''}
              onFocus={() => {
                handleProjectWorkspaceButtonClick('PROJECT_LIST');
              }}
              ariaLabel={t('reports.selectionModal.selectProject')}
              onBlur={() => setFocusedTextField(null)}
              inputProps={{
                ref: projectButtonRef,
                tabIndex: 0,
                id: 'project-button',
              }}
              InputProps={{
                readOnly: true,
                disableUnderline: true,
                tabIndex: -1,
              }}
              data-testid="project-button"
            />
            <AurTextField
              variant="filled"
              className={`aur-textfield custom-text-field ${selectedListType === 'WORKSPACE_LIST' ? 'focused' : ''}`}
              label={t('reports.selectionModal.selectWorkspace')}
              placeholder={t('reports.selectionModal.chooseFromList')}
              onFocus={() => {
                handleProjectWorkspaceButtonClick('WORKSPACE_LIST');
              }}
              onBlur={() => setFocusedTextField(null)}
              value={selectedWorkspace?.name ?? ''}
              ariaLabel={t('reports.selectionModal.selectWorkspace')}
              inputProps={{
                ref: workspaceButtonRef,
                tabIndex: focusedTextField === 'PROJECT_LIST' ? -1 : 0,
                id: 'workspace-button',
              }}
              InputProps={{
                readOnly: true,
                disableUnderline: true,
                tabIndex: -1,
              }}
              tabIndex={-1}
              data-testid="workspace-button"
            />
          </div>
          <div className="table-container">
            <style>
              {`
        /* Chrome, Edge, and Safari */
        div::-webkit-scrollbar {
          display: none;
        }
      `}
            </style>
            <AurDataGrid
              key={`key-${noOfItemPerPage}`}
              className="listing-data-table"
              rows={tableRows}
              totalRowCount={totalRowCount}
              columns={columns}
              checkBoxVisible={false}
              visibleToolTip={true}
              entityType={t('common.reportsModel')}
              pointer
              onSearch={onSearch}
              searchValue={searchTerm}
              onRowClick={handleRowSelection}
              getRowId={(row) => row.id}
              getRowHeight={() => 56}
              columnHeaderHeight={64}
              selectedIds={selectedRowId ? [selectedRowId] : []}
              currentPage={currentPageNo}
              setCurrentPage={setCurrentPageNo}
              itemsPerPage={noOfItemPerPage}
              setItemsPerPage={handlePageSizeChange}
              showPagination={false}
              noRowsOverlay={AurDataGridLoadingOverlay}
              aria-live="polite"
              aria-label={t(
                'accessibility.table.' + selectedListType === 'PROJECT_LIST'
                  ? 'projectList'
                  : 'wokrspaceList'
              )}
              headerSpanTabIndex={-1}
              defaultHeaderCellTabIndex={-1}
              enableRowClickOnEnterPress
              disableHeaderRowTabIndex
              addedInDialog
              classNameOfParentScollElement="table-container"
            />
          </div>
        </>
      </DialogContent>
      <DialogActions className="footer-action-container">
        <AurPagination
          totalItems={totalRowCount}
          itemsPerPage={noOfItemPerPage}
          currentPage={currentPageNo}
          perPageOptions={perPageOptions}
          itemType={t('common.selectionList')}
          setItemsPerPage={handlePageSizeChange}
          onPageChange={setCurrentPageNo}
          includeCancelContinueEvents
          handleContinueClick={handleContinueClick}
          handleCancelClick={handleCancelClick}
          continuButtonDisabled={!selectedProject || !selectedWorkspace}
          backgroundColor="#0C1119"
          canClearAriaLabel={t(
            'reports.hostingUserSummary.cancleSelectionModalHint',
            {
              screen:
                dialogOpenCount === 1
                  ? t('reports.hostingUserSummary.welcomeClient')
                  : openFrom + ' report',
            }
          )}
          continueButtonHint={t(
            `reports.hostingUserSummary.${!selectedProject || !selectedWorkspace ? 'makeContinueButtonEnableHint' : 'contnueButtonHint'}`
          )}
          trackPaginationActionName={t('gaTracking.global.reportPagination')}
        />
      </DialogActions>
    </>
  );
};

export default ProjectWorkSpaceSelection;
